import React, { useEffect, useRef, useState } from 'react';
import { trackEvent } from '../utils/tracking';
const BACKEND_URL = 'https://mercadopago-her-backend.vercel.app';
const MP_PUBLIC_KEY = 'APP_USR-2e5e9ad0-2e5a-4077-b86b-9ae196fe76ec';
interface PaymentBrickCTAProps {
  email?: string;
  amount?: number;
  title?: string;
  productType?: string;
  onApproved?: () => void;
}
export function PaymentBrickCTA({
  email = '',
  amount = 12000,
  title = 'Protocolo H.E.R. — Acceso Completo',
  productType = 'her',
  onApproved
}: PaymentBrickCTAProps) {
  const rendered = useRef(false);
  const controllerRef = useRef<any>(null);
  const pollTimer = useRef<number | null>(null);
  const [status, setStatus] = useState<'loading' | 'processing' | 'approved' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const getCookie = (name: string) => {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : undefined;
  };
  const generateEventId = () => `HER-PB-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
  useEffect(() => () => {
    if (pollTimer.current) window.clearTimeout(pollTimer.current);
    try {
      controllerRef.current?.unmount?.();
    } catch {}
  }, []);
  useEffect(() => {
    if (rendered.current) return;
    rendered.current = true;
    const fail = (message: string) => {
      console.error('No se pudo inicializar Payment Brick:', message);
      setErrorMessage(message);
      setStatus('error');
      trackEvent('payment_brick_error', {
        section: 'offer',
        message
      });
    };
    const confirmPayment = async (paymentId: string, attempt = 0) => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/payment-status?payment_id=${encodeURIComponent(paymentId)}`);
        const data = await response.json();
        if (data.status === 'approved') {
          setStatus('approved');
          onApproved?.();
          return;
        }
        if (data.status === 'rejected' || data.status === 'cancelled') {
          setStatus('error');
          setErrorMessage(`Pago ${data.status}`);
          return;
        }
        if (attempt < 15) pollTimer.current = window.setTimeout(() => confirmPayment(paymentId, attempt + 1), 2000);
      } catch {
        if (attempt < 15) pollTimer.current = window.setTimeout(() => confirmPayment(paymentId, attempt + 1), 2000);else fail('No se pudo confirmar el estado del pago');
      }
    };
    const createPreference = async () => {
      const eventId = generateEventId();
      const payload = JSON.stringify({
        title,
        quantity: 1,
        unit_price: amount,
        external_reference: eventId,
        event_id: eventId,
        email: email || undefined,
        fbp: getCookie('_fbp'),
        fbc: getCookie('_fbc'),
        product_type: productType
      });
      const response = await fetch(`${BACKEND_URL}/api/create-preference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: payload
      });
      const text = await response.text();
      let data: any = {};
      try {
        data = JSON.parse(text);
      } catch {}
      if (!response.ok || !data.preference_id) throw new Error(`Preferencia: HTTP ${response.status}${data.error ? ` — ${data.error}` : text ? ` — ${text.slice(0, 180)}` : ''}`);
      return data.preference_id as string;
    };
    const initBrick = async () => {
      if (!(window as any).MercadoPago) return fail('SDK de Mercado Pago no disponible');
      try {
        const preferenceId = await createPreference();
        const mp = new (window as any).MercadoPago(MP_PUBLIC_KEY, {
          locale: 'es-AR'
        });
        const bricksBuilder = mp.bricks();
        controllerRef.current = await bricksBuilder.create('payment', `payment_brick_${productType}`, {
          initialization: {
            amount,
            preferenceId,
            payer: {
              email: email || undefined
            }
          },
          customization: {
            visual: {
              style: {
                theme: 'dark'
              }
            },
            paymentMethods: {
              creditCard: 'all',
              debitCard: 'all',
              ticket: 'all',
              mercadoPago: 'all'
            }
          },
          callbacks: {
            onReady: () => {
              setStatus('loading');
              trackEvent('payment_brick_ready', {
                section: 'offer',
                product_type: productType,
                amount
              });
            },
            onSubmit: async ({
              selectedPaymentMethod,
              formData
            }: any) => {
              if (selectedPaymentMethod === 'mercadoPago') {
                trackEvent('payment_brick_wallet_selected', {
                  section: 'offer',
                  method: selectedPaymentMethod,
                  product_type: productType,
                  amount
                });
                return;
              }
              const paymentEventId = generateEventId();
              setStatus('processing');
              trackEvent('begin_checkout', {
                section: 'offer',
                method: 'payment_brick',
                product_type: productType,
                amount
              }, {
                eventId: paymentEventId
              });
              try {
                const response = await fetch(`${BACKEND_URL}/api/test-payment-brick`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    formData,
                    email: email || undefined,
                    event_id: paymentEventId,
                    fbp: getCookie('_fbp'),
                    fbc: getCookie('_fbc'),
                    currency: 'ARS',
                    value: amount,
                    product_type: productType,
                    title
                  })
                });
                const text = await response.text();
                let data: any = {};
                try {
                  data = JSON.parse(text);
                } catch {}
                if (!response.ok || !data.success) throw new Error(`Pago: HTTP ${response.status}${data.error ? ` — ${data.error}` : text ? ` — ${text.slice(0, 180)}` : ''}`);
                trackEvent('payment_created', {
                  section: 'offer',
                  status: data.status,
                  payment_id: data.payment_id,
                  product_type: productType,
                  amount
                }, {
                  eventId: paymentEventId
                });
                if (data.payment_id) confirmPayment(String(data.payment_id));
              } catch (error: any) {
                setStatus('error');
                setErrorMessage(error?.message || 'No se pudo procesar el pago');
                throw error;
              }
            },
            onError: (error: any) => fail(`Payment Brick: ${error?.message || error?.code || JSON.stringify(error).slice(0, 180)}`)
          }
        });
      } catch (error: any) {
        fail(error?.message || String(error));
      }
    };
    const waitForSdk = () => {
      if ((window as any).MercadoPago) return initBrick();
      const existing = document.querySelector('script[src="https://sdk.mercadopago.com/js/v2"]') as HTMLScriptElement | null;
      if (existing) return existing.addEventListener('load', initBrick, {
        once: true
      });
      const script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.async = true;
      script.onload = initBrick;
      script.onerror = () => fail('No se pudo cargar el SDK de Mercado Pago');
      document.head.appendChild(script);
    };
    waitForSdk();
  }, [email, amount, title, productType, onApproved]);
  return <div className="w-full space-y-4" data-id="element-629" data-mp="1677rh8mpql6c"><div id={`payment_brick_${productType}`} className="w-full text-left" data-id="element-630" data-mp="1b00t3nmvxde7" />{status === 'loading' && <p className="text-xs text-[#F0E8D8]/45 text-center" data-id="element-631" data-mp="wbmgwm3kwckz">Cargando opciones de pago…</p>}{status === 'processing' && <p className="text-xs text-[#C8A870] text-center" data-id="element-632" data-mp="11azfu57q5a3x">Procesando el pago…</p>}{status === 'approved' && <p className="text-xs text-[#C8A870] text-center" data-id="element-633" data-mp="1p0jyemzyu5co">Pago aprobado.</p>}{status === 'error' && <p className="text-[11px] text-red-300 text-center break-words" data-id="element-634" data-mp="1tyval4dqgry2">Error técnico: {errorMessage}</p>}</div>;
}